/* ════════════ THE SEAL ════════════
   The privacy page's atmosphere: fragments of a life drift up in
   plaintext, then encrypt left-to-right into ciphertext and fade —
   envelopes sealing themselves before anyone gets a look. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function seal(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#seal")!;
  const ctx = c.getContext("2d")!;

  const WORDS = [
    "dear diary", "ballot", "diagnosis", "first draft", "love letter",
    "salary", "location", "browsing", "who i met", "what i fear",
    "old beliefs", "3am search", "prayer", "therapy notes", "the joke",
    "my people", "savings", "doubts", "passwords", "what i read",
  ];
  const CIPHER = "0123456789abcdef█▓▒░#$%&";
  const AMBER = "#ffd23e";
  const GREY = "#9aa0a6";

  type Note = {
    x: number;
    y: number;
    t: number; // age in frames
    life: number;
    size: number;
    col: string;
    plain: string;
    cipher: string;
  };
  let notes: Note[] = [];

  const scramble = (len: number) => {
    let s = "";
    for (let i = 0; i < len; i++) s += CIPHER[(Math.random() * CIPHER.length) | 0];
    return s;
  };

  const spawn = (): Note => {
    const isAmber = Math.random() < 0.15;
    const plain = WORDS[(Math.random() * WORDS.length) | 0]!;
    return {
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      t: 0,
      life: 200 + Math.random() * 260,
      size: isAmber ? 13 + Math.random() * 6 : 10 + Math.random() * 4,
      col: isAmber ? AMBER : GREY,
      plain,
      cipher: scramble(plain.length),
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(48, Math.floor((c.width * c.height) / 38000));
    notes = Array.from({ length: count }, () => {
      const n = spawn();
      n.t = Math.random() * n.life; // desync
      return n;
    });
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const n of notes) {
      const p = n.t / n.life;
      // fade in, hold readable, then the seal closes and it dissolves
      const a = p < 0.12 ? p / 0.12 : p > 0.75 ? (1 - p) / 0.25 : 1;
      // encryption sweep: from 35% of life, characters lock left-to-right
      const locked = p < 0.35 ? 0 : Math.ceil(((p - 0.35) / 0.4) * n.plain.length);
      const txt = n.cipher.slice(0, locked) + n.plain.slice(locked);
      ctx.globalAlpha = a * (n.col === AMBER ? 0.5 : 0.22);
      ctx.fillStyle = n.col;
      ctx.font = `${n.size}px "IBM Plex Mono", monospace`;
      ctx.fillText(txt, n.x, n.y);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < notes.length; i++) {
      const n = notes[i]!;
      n.t++;
      n.y -= 0.06; // drifting up, mail leaving the building
      if (n.t >= n.life) notes[i] = spawn();
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(seal);
